/** The topics a venue channel can carry (spec/09 §3 and §6.3). */
export const VENUE_CHANNEL_TOPICS = [
    'floor',
    'orders',
    'kitchen',
    'calls',
    'reservations',
    'public',
    'display',
    // Clavian Boot's live view for the console (spec/25 §14, spec/09 §6.3): `boot.server.changed`, `boot.pc.changed`
    // and `boot.job.progress`, to a member who holds `boot:view` at the venue.
    'boot',
];
/** The topics a station channel can carry beyond the station's own channel (spec/09 §3). */
export const STATION_CHANNEL_TOPICS = ['netstats', 'signal'];
const TYPE_ID_FRAGMENT = '[a-z][a-z_]*_[0-7][0123456789abcdefghjkmnpqrstvwxyz]{25}';
export const CHANNEL_PATTERN = new RegExp([
    `^station:${TYPE_ID_FRAGMENT}(?::(?:${STATION_CHANNEL_TOPICS.join('|')}))?$`,
    `^member:${TYPE_ID_FRAGMENT}$`,
    `^player:${TYPE_ID_FRAGMENT}$`,
    `^venue:${TYPE_ID_FRAGMENT}:(?:${VENUE_CHANNEL_TOPICS.join('|')})$`,
    `^org:${TYPE_ID_FRAGMENT}:alerts$`,
    `^display:${TYPE_ID_FRAGMENT}$`,
    `^boot-server:${TYPE_ID_FRAGMENT}$`,
].join('|'));
/** Constructors, so a channel name is never assembled with string concatenation at a call site. */
export const channels = {
    station: (stationId, topic) => (topic === undefined ? `station:${stationId}` : `station:${stationId}:${topic}`),
    member: (memberId) => `member:${memberId}`,
    player: (playerId) => `player:${playerId}`,
    venue: (venueId, topic) => `venue:${venueId}:${topic}`,
    organizationAlerts: (organizationId) => `org:${organizationId}:alerts`,
    /** A staff-run station's countdown screen (ADR-0033): the one channel a `display` principal holds. */
    display: (stationId) => `display:${stationId}`,
    /**
     * A Clavian Boot server's own channel (ADR-0063, spec/25 §14): the one channel a `boot_server` principal holds, and
     * what the api sends it on — `boot.config.updated`, `boot.wipe`, `boot.edit.*`, `boot.capture.start`, `boot.wol.send`,
     * `boot.shader.promote`.
     */
    bootServer: (bootServerId) => `boot-server:${bootServerId}`,
};
export function isChannel(value) {
    return typeof value === 'string' && CHANNEL_PATTERN.test(value);
}
// Message-type names (the `type` discriminant of each realtime message, spec/09 §6). Each is defined
// here and imported by the schema file that models the payload, so a client can switch on the name
// without importing the schema.
export const ACCESS_CHANGED = 'access.changed';
export const ACCESS_REVOKED = 'access.revoked';
export const ALERTS_NEW = 'alerts.new';
export const ALERTS_CLEARED = 'alerts.cleared';
export const COMMAND_UPDATED = 'command.updated';
export const FLOOR_STATION_UPDATED = 'floor.station.updated';
// A session started or ended at the venue, on `venue:{id}:floor` (spec/09 §6.3, F-ANL-02): the floor's live figures
// (occupancy, sessions, takings) re-read on them.
export const FLOOR_SESSION_STARTED = 'floor.session.started';
export const FLOOR_SESSION_ENDED = 'floor.session.ended';
export const FLOOR_PLAN_UPDATED = 'floor.plan.updated';
// A boot PC's state may have changed (step C of ADR-0065), on `venue:{id}:floor`: no boot detail, for floor-only readers.
export const FLOOR_BOOT_CHANGED = 'floor.boot.changed';
// A staff-run station's countdown screen should re-read (ADR-0033), on `display:{stationId}`. Carries no data.
export const DISPLAY_CHANGED = 'display.changed';
export const KITCHEN_TICKET_CREATED = 'kitchen.ticket.created';
export const KITCHEN_TICKET_UPDATED = 'kitchen.ticket.updated';
export const ORDERS_CREATED = 'orders.created';
export const ORDERS_UPDATED = 'orders.updated';
// Calls for help on `venue:{id}:calls` (spec/09 §6.3, F-SES-25): here so a console can switch on them without Zod.
export const CALLS_OPENED = 'calls.opened';
export const CALLS_UPDATED = 'calls.updated';
export const CALLS_CLOSED = 'calls.closed';
// Clavian Boot's live view on `venue:{id}:boot` (spec/25 §14, spec/09 §6.3): here so a console can switch on them without Zod.
export const BOOT_SERVER_CHANGED = 'boot.server.changed';
export const BOOT_PC_CHANGED = 'boot.pc.changed';
export const BOOT_JOB_PROGRESS = 'boot.job.progress';
// Connection-control message names (spec/09 §1, §5) — the frames a client sends and the ones it acts
// on directly, so the transport can name them without importing the connection schemas.
export const AUTH_CONNECT = 'auth.connect';
export const AUTH_REFRESH = 'auth.refresh';
export const AUTH_EXPIRING = 'auth.expiring';
export const CHANNEL_SUBSCRIBE = 'channel.subscribe';
export const CHANNEL_UNSUBSCRIBE = 'channel.unsubscribe';
export const REALTIME_PING = 'realtime.ping';
export const REALTIME_PONG = 'realtime.pong';
/** `evt`, on no channel: it is about this connection, not anything a client subscribed to (spec/09 §8). */
export const SERVER_DRAINING = 'server.draining';
/**
 * The WebSocket close codes spec/09 §8 fixes, in one place, so the gateway, the clients and the tests
 * read the same ones. A client switches on these to decide whether to reconnect, sign out or go to the
 * pairing screen — so they must be reachable without pulling the envelope schemas.
 */
export const REALTIME_CLOSE_CODES = {
    normal: 1000,
    goingAway: 1001,
    slowConsumer: 1013,
    unauthenticated: 4401,
    forbidden: 4403,
    replaced: 4409,
    frameTooLarge: 4413,
    rateLimited: 4429,
};
//# sourceMappingURL=constants.js.map